import { Shield, Trash2 } from 'lucide-react';

import { useTranslation } from '../../i18n';
import { useDeleteUser } from '../../hooks/queries/useUserQueries';
import { IUser } from '../../types';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

interface IUserDeleteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  user: IUser | null;
  onDeleted?: () => void;
}

const UserDeleteDialog = ({
  isOpen,
  onClose,
  user,
  onDeleted,
}: IUserDeleteDialogProps) => {
  const { t } = useTranslation();
  const deleteUser = useDeleteUser();

  const handleConfirm = async () => {
    if (!user) return;

    await deleteUser.mutateAsync(user.id);
    onClose();
    onDeleted?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="sm:max-w-[420px]"
        data-testid="UserManagement_List_deleteModal"
      >
        <DialogHeader>
          <DialogTitle>{t('users.delete_title')}</DialogTitle>
          <DialogDescription>
            {t('users.delete_confirm', { name: user?.displayName ?? '' })}
          </DialogDescription>
        </DialogHeader>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-red-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-900">
                {t('users.delete_warning')}
              </p>
              <p className="text-sm text-red-700 mt-1">
                {t('users.delete_warning_text')}
              </p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="UserManagement_List_deleteModalCancelButton"
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={deleteUser.isPending}
            data-testid="UserManagement_List_deleteModalConfirmButton"
          >
            <Trash2 className="w-4 h-4" />
            {deleteUser.isPending
              ? t('common.saving')
              : t('users.confirm_delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserDeleteDialog;
